import React from 'react';
import {
  PencilLine, BookOpen, Library, Users, UsersRound, Sparkles, TrendingUp,
  Trophy, ShoppingBag, ShoppingCart, MessageCircle, Star, ShieldCheck, Crown, Bot,
  Lock
} from 'lucide-react';
import { useLang } from '@/context/LangContext';

const ICONS = {
  PencilLine, BookOpen, Library, Users, UsersRound, Sparkles, TrendingUp,
  Trophy, ShoppingBag, ShoppingCart, MessageCircle, Star, ShieldCheck, Crown, Bot,
};

const TIER_STYLES = {
  bronze:  { ring: 'border-amber-700/70',  text: 'text-amber-500',  bg: 'bg-amber-900/10',  k: 'tier_bronze' },
  silver:  { ring: 'border-slate-400/80',  text: 'text-slate-200',  bg: 'bg-slate-700/20',  k: 'tier_silver' },
  gold:    { ring: 'border-gold',          text: 'text-gold',       bg: 'bg-gold/15',       k: 'tier_gold' },
  elite:   { ring: 'border-fuchsia-400',   text: 'text-fuchsia-300',bg: 'bg-fuchsia-900/20',k: 'tier_elite' },
};

export function Badge({ badge, unlocked = true, unlockedAt, size = 'md' }) {
  const { t } = useLang();
  const Icon = ICONS[badge.icon] || Trophy;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
  const dim = size === 'sm' ? { box: 'w-16', icon: 'w-6 h-6', txt: 'text-[10px]' } :
              size === 'lg' ? { box: 'w-28', icon: 'w-10 h-10', txt: 'text-xs' } :
                              { box: 'w-20', icon: 'w-7 h-7', txt: 'text-[11px]' };

  return (
    <div className={`${dim.box} flex flex-col items-center text-center group`} title={badge.desc}>
      <div className={`relative aspect-square w-full rounded-2xl border-2 ${tier.ring} ${unlocked ? tier.bg : 'bg-navy-deep'} flex items-center justify-center transition ${unlocked ? '' : 'opacity-40 grayscale'}`}>
        {unlocked ? (
          <Icon className={`${dim.icon} ${tier.text}`} strokeWidth={1.8} />
        ) : (
          <Lock className={`${dim.icon} text-gray-500`} strokeWidth={1.8} />
        )}
        {unlocked && (
          <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 ${tier.bg} border ${tier.ring} ${tier.text} text-[8px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 rounded`}>
            {t(tier.k)}
          </div>
        )}
      </div>
      <div className={`mt-3 font-h uppercase ${dim.txt} leading-tight ${unlocked ? 'text-white' : 'text-gray-500'}`}>{badge.name}</div>
    </div>
  );
}

export default function BadgeCabinet({ unlocked = [], catalog = [], showLocked = true, max = null, size = 'md' }) {
  const { t } = useLang();
  const unlockedIds = new Set(unlocked.map(u => u.badge_id || u.badge?.id));
  const list = catalog.length ? catalog : unlocked.map(u => u.badge);
  const items = list.map(b => ({
    badge: b,
    unlocked: unlockedIds.has(b.id),
    unlockedAt: unlocked.find(u => (u.badge_id || u.badge?.id) === b.id)?.unlocked_at,
  }));
  const visible = showLocked ? items : items.filter(i => i.unlocked);
  const sliced = max ? visible.slice(0, max) : visible;

  if (!sliced.length) {
    return <div className="text-xs text-gray-500 italic">{t('no_badges_yet')}</div>;
  }
  return (
    <div className={`grid gap-4 ${size === 'sm' ? 'grid-cols-4 sm:grid-cols-6 lg:grid-cols-8' : 'grid-cols-3 sm:grid-cols-5 lg:grid-cols-6'}`}>
      {sliced.map((it) => (
        <Badge key={it.badge.id} badge={it.badge} unlocked={it.unlocked} unlockedAt={it.unlockedAt} size={size} />
      ))}
    </div>
  );
}
